/**
 * AvailabilityEditor — переиспользуемый контролируемый редактор
 * Product.availability / Category.availability (B2 dlvry-catalog).
 *
 * Контракт (backend: app/schemas/catalog_policy.py AvailabilityPolicy):
 *   { channels?, schedule?: {weekdays?, time_from?, time_to?},
 *     min_quantity?, max_quantity?,
 *     max_discount_percent?, max_bonus_spend_percent?, max_bonus_earn_percent? }
 * Backend читает dict как AvailabilityPolicy(**v) — лишние ключи запрещены
 * (extra=forbid), поэтому наружу отдаём только известные поля.
 *
 * Канонические каналы на backend — ТОЛЬКО website/tg/vk (см. _CANONICAL_CHANNELS
 * в catalog_policy.py; telegram/vkontakte/web — алиасы, нормализуемые к tg/vk/website).
 * "mobile"/"max" канала backend не принимает — такого выбора здесь нет,
 * см. docs/B2_ADMIN_UI.md.
 *
 * Используется как AntD controlled-value компонент внутри Form.Item name="availability":
 * value/onChange — стандартный контракт, форма сама кладёт значение в values.availability.
 */
import React, { useState } from 'react';
import { Alert, Button, Checkbox, Col, Collapse, InputNumber, Row, Space, Switch, Typography } from 'antd';
import ScheduleEditor from './ScheduleEditor';
import {
  AvailabilityPolicyValue, AvailabilitySchedule, CANONICAL_CHANNELS, WEEKDAYS,
  validateAvailabilityPolicy, validateSchedule,
} from './availabilityTypes';

export type { AvailabilityPolicyValue, AvailabilitySchedule };
export { CANONICAL_CHANNELS, WEEKDAYS, validateAvailabilityPolicy, validateSchedule };

const { Text } = Typography;

interface Props {
  value?: AvailabilityPolicyValue | null;
  onChange?: (value: AvailabilityPolicyValue | null) => void;
  disabled?: boolean;
}

const SCHEDULE_NOTE =
  'Расписание интерпретируется в часовом поясе филиала (branch.timezone). ' +
  'Если филиал ещё не выбран/не привязан — используется устаревший fallback Europe/Moscow ' +
  '(требует согласования с backend при интеграции, см. docs/B2_ADMIN_UI.md).';

const AvailabilityEditor: React.FC<Props> = ({ value, onChange, disabled }) => {
  const enabled = value != null;
  const [localError, setLocalError] = useState<string | null>(null);

  const emit = (next: AvailabilityPolicyValue | null) => {
    const err = validateAvailabilityPolicy(next);
    setLocalError(err);
    onChange?.(next);
  };

  const toggleEnabled = (checked: boolean) => {
    emit(checked ? {} : null);
  };

  const patch = (partial: Partial<AvailabilityPolicyValue>) => {
    emit({ ...(value ?? {}), ...partial });
  };

  const clearSection = (...keys: (keyof AvailabilityPolicyValue)[]) => {
    const next = { ...(value ?? {}) };
    keys.forEach((k) => delete next[k]);
    emit(next);
  };

  const channels = value?.channels ?? [];
  const schedule = value?.schedule ?? {};

  return (
    <div>
      <Space align="center" style={{ marginBottom: 8 }}>
        <Switch
          data-testid="availability-enabled-switch"
          checked={enabled}
          disabled={disabled}
          onChange={toggleEnabled}
        />
        <Text>{enabled ? 'Ограничения доступности включены' : 'Без собственных ограничений (настройки категории и её предков сохраняются)'}</Text>
      </Space>

      {enabled && (
        <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 16 }}>
          {localError && <Alert type="error" showIcon message={localError} style={{ marginBottom: 12 }} />}

          <Collapse
            ghost
            defaultActiveKey={['channels', 'schedule', 'quantity', 'caps']}
            items={[
              {
                key: 'channels',
                label: 'Каналы продаж',
                children: (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Checkbox.Group
                      disabled={disabled}
                      options={CANONICAL_CHANNELS}
                      value={channels}
                      onChange={(v) => patch({ channels: (v as string[]).length ? (v as string[]) : undefined })}
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Если ни один канал не выбран — доступен во всех каналах.
                    </Text>
                    {channels.length > 0 && (
                      <Button size="small" onClick={() => clearSection('channels')} disabled={disabled}>
                        Очистить каналы
                      </Button>
                    )}
                  </Space>
                ),
              },
              {
                key: 'schedule',
                label: 'Расписание (дни недели / время)',
                children: (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <ScheduleEditor
                      value={schedule}
                      disabled={disabled}
                      note={SCHEDULE_NOTE}
                      onChange={(sched) => patch({ schedule: sched })}
                    />
                    {(schedule.weekdays?.length || schedule.time_from || schedule.time_to) && (
                      <Button size="small" onClick={() => clearSection('schedule')} disabled={disabled}>
                        Очистить расписание
                      </Button>
                    )}
                  </Space>
                ),
              },
              {
                key: 'quantity',
                label: 'Мин/макс количество в заказе',
                children: (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Row gutter={8}>
                      <Col span={12}>
                        <Text style={{ display: 'block', marginBottom: 4 }}>Минимум</Text>
                        <InputNumber
                          min={1}
                          step={1}
                          style={{ width: '100%' }}
                          disabled={disabled}
                          value={value?.min_quantity ?? undefined}
                          onChange={(v) => patch({ min_quantity: v == null ? undefined : Math.trunc(v as number) })}
                        />
                      </Col>
                      <Col span={12}>
                        <Text style={{ display: 'block', marginBottom: 4 }}>Максимум</Text>
                        <InputNumber
                          min={1}
                          step={1}
                          style={{ width: '100%' }}
                          disabled={disabled}
                          value={value?.max_quantity ?? undefined}
                          onChange={(v) => patch({ max_quantity: v == null ? undefined : Math.trunc(v as number) })}
                        />
                      </Col>
                    </Row>
                    {(value?.min_quantity != null || value?.max_quantity != null) && (
                      <Button size="small" onClick={() => clearSection('min_quantity', 'max_quantity')} disabled={disabled}>
                        Очистить мин/макс
                      </Button>
                    )}
                  </Space>
                ),
              },
              {
                key: 'caps',
                label: 'Ограничения скидок и бонусов (%)',
                children: (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Alert
                      type="info"
                      showIcon
                      message="Лимиты скидок и бонусов"
                      description={
                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                          <li>Действует самый строгий лимит: свой, категории или её предков. Пустое значение снимает только свой лимит; 0% запрещает, а 100% не отменяет более строгий унаследованный лимит.</li>
                          <li>Списание бонусов: лимит от суммы позиции после скидки.</li>
                          <li>Начисление бонусов: лимит от суммы позиции до скидок. Процент программы берётся при завершении заказа; лимит товара фиксируется при оформлении.</li>
                          <li>Лимит одной позиции не переносится на другие. Подарки и доставка не участвуют.</li>
                        </ul>
                      }
                    />
                    <Row gutter={8}>
                      <Col span={8}>
                        <label htmlFor="availability-cap-discount" style={{ display: 'block', marginBottom: 4 }}>
                          Макс. скидка, %
                        </label>
                        <InputNumber
                          id="availability-cap-discount"
                          min={0}
                          max={100}
                          style={{ width: '100%' }}
                          disabled={disabled}
                          value={value?.max_discount_percent ?? undefined}
                          onChange={(v) => patch({ max_discount_percent: v == null ? undefined : (v as number) })}
                        />
                      </Col>
                      <Col span={8}>
                        <label htmlFor="availability-cap-bonus-spend" style={{ display: 'block', marginBottom: 4 }}>
                          Макс. списание бонусов, %
                        </label>
                        <InputNumber
                          id="availability-cap-bonus-spend"
                          min={0}
                          max={100}
                          style={{ width: '100%' }}
                          disabled={disabled}
                          value={value?.max_bonus_spend_percent ?? undefined}
                          onChange={(v) => patch({ max_bonus_spend_percent: v == null ? undefined : (v as number) })}
                        />
                      </Col>
                      <Col span={8}>
                        <label htmlFor="availability-cap-bonus-earn" style={{ display: 'block', marginBottom: 4 }}>
                          Макс. начисление бонусов, %
                        </label>
                        <InputNumber
                          id="availability-cap-bonus-earn"
                          min={0}
                          max={100}
                          style={{ width: '100%' }}
                          disabled={disabled}
                          value={value?.max_bonus_earn_percent ?? undefined}
                          onChange={(v) => patch({ max_bonus_earn_percent: v == null ? undefined : (v as number) })}
                        />
                      </Col>
                    </Row>
                    {(value?.max_discount_percent != null ||
                      value?.max_bonus_spend_percent != null ||
                      value?.max_bonus_earn_percent != null) && (
                      <Button
                        size="small"
                        onClick={() => clearSection('max_discount_percent', 'max_bonus_spend_percent', 'max_bonus_earn_percent')}
                        disabled={disabled}
                      >
                        Очистить проценты
                      </Button>
                    )}
                  </Space>
                ),
              },
            ]}
          />
        </div>
      )}
    </div>
  );
};

export default AvailabilityEditor;
